import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Phone, Mail, MapPin, Clock, CheckCircle, Award } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

// 🔒 Force l'appel vers l'API Vercel
const API = '/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    email: '',
    ville: '',
    type_toiture: '',
    objet: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/devis`, formData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data?.ok) {
        toast({
          title: "Demande envoyée avec succès !",
          description: "Nous revenons vers vous sous 24–48h avec votre devis personnalisé.",
          variant: "default",
        });

        setFormData({
          nom: '',
          telephone: '',
          email: '',
          ville: '',
          type_toiture: '',
          objet: '',
          message: ''
        });
      } else {
        throw new Error(response.data?.error || 'Envoi impossible');
      }
    } catch (error) {
      console.error('Erreur envoi formulaire:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        "Une erreur est survenue. Veuillez réessayer.";

      toast({
        title: "Erreur d'envoi",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallClick = () => {
    // TODO: remplace par ton vrai numéro, ex: tel:+33612345678
    window.location.href = 'tel:[TEL]';
  };

  const handleEmailClick = () => {
    // TODO: remplace par ton vrai email, ex: contact@marque.fr
    window.location.href = 'mailto:contact@[MARQUE].fr';
  };

  return (
    <Layout>
      {/* Header Section */}
      <section className="hero section-sm">
        <div className="hero-content">
          <div className="container text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] mb-6">
              Demandez votre devis gratuit
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Obtenez une réponse personnalisée sous 24-48h pour tous vos travaux de toiture, 
              zinguerie et étanchéité sur Toulouse et l'Occitanie.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card className="p-8">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 font-['Outfit'] mb-4">
                    Formulaire de contact
                  </h2>
                  <p className="text-gray-600">
                    Décrivez votre projet et recevez votre devis détaillé gratuitement
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Nom complet *</label>
                      <Input
                        value={formData.nom}
                        onChange={(e) => handleInputChange('nom', e.target.value)}
                        required
                        className="form-input"
                        placeholder="Votre nom et prénom"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Téléphone *</label>
                      <Input
                        type="tel"
                        value={formData.telephone}
                        onChange={(e) => handleInputChange('telephone', e.target.value)}
                        required
                        className="form-input"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Email *</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        className="form-input"
                        placeholder="vous@exemple.fr"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Ville *</label>
                      <Input
                        value={formData.ville}
                        onChange={(e) => handleInputChange('ville', e.target.value)}
                        required
                        className="form-input"
                        placeholder="Votre ville"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label">Type de toiture</label>
                      <Select
                        value={formData.type_toiture}
                        onValueChange={(value) => handleInputChange('type_toiture', value)}
                      >
                        <SelectTrigger className="form-input">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tuiles">Tuiles</SelectItem>
                          <SelectItem value="ardoises">Ardoises</SelectItem>
                          <SelectItem value="bac-acier">Bac acier</SelectItem>
                          <SelectItem value="toit-terrasse">Toit-terrasse</SelectItem>
                          <SelectItem value="autres">Autres</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Type de prestation</label>
                      <Select
                        value={formData.objet}
                        onValueChange={(value) => handleInputChange('objet', value)}
                      >
                        <SelectTrigger className="form-input">
                          <SelectValue placeholder="Sélectionnez" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="refection">Réfection toiture</SelectItem>
                          <SelectItem value="zinguerie">Zinguerie</SelectItem>
                          <SelectItem value="etancheite">Étanchéité</SelectItem>
                          <SelectItem value="nettoyage">Nettoyage/Démoussage</SelectItem>
                          <SelectItem value="depannage">Dépannage urgence</SelectItem>
                          <SelectItem value="devis-general">Devis général</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Description de votre projet</label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      rows={4}
                      className="form-input"
                      placeholder="Décrivez votre projet, les problèmes constatés, vos besoins..."
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full btn btn-primary text-lg py-4"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Recevoir mon devis sous 24-48h'}
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    * Champs obligatoires. Devis gratuit ≤30 km autour de Cugnaux.
                    <br />
                    En soumettant ce formulaire, j'accepte d'être contacté pour mon devis.
                  </p>
                </form>
              </Card>
            </div>

            {/* Contact Info & Map */}
            <div className="space-y-8">
              {/* Contact Info */}
              <Card className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 font-['Outfit'] mb-6">
                  Nos coordonnées
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Téléphone</div>
                      <button 
                        type="button"
                        onClick={handleCallClick}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        [TEL]
                      </button>
                      <div className="text-sm text-gray-600 mt-1">
                        Lun–Ven 8h–18h • Urgences 24/7
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Email</div>
                      <button 
                        type="button"
                        onClick={handleEmailClick}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        contact@[MARQUE].fr
                      </button>
                      <div className="text-sm text-gray-600 mt-1">
                        Réponse sous 24–48h
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Adresse</div>
                      <div className="text-gray-600">
                        [ADRESSE]<br />
                        31270 Cugnaux
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Intervention Toulouse & Occitanie
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Horaires</div>
                      <div className="text-gray-600">Lun–Ven 8h–18h</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Garanties</div>
                      <div className="text-gray-600">Assurance décennale & RC Pro</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Award className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Engagements</div>
                      <div className="text-gray-600">Respect des DTU • Intervention à signature</div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Zone d'intervention */}
              <Card className="p-8">
                <h3 className="text-xl font-bold text-gray-900 font-['Outfit'] mb-4">
                  Zone d'intervention
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <div className="font-semibold text-gray-900 mb-2">Devis gratuit (≤30 km) :</div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>• Toulouse</div>
                      <div>• Cugnaux</div>
                      <div>• Colomiers</div>
                      <div>• Tournefeuille</div>
                      <div>• Blagnac</div>
                      <div>• Balma</div>
                      <div>• Muret</div>
                      <div>• Seysses</div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <div className="font-semibold text-blue-600">+ Toute l'Occitanie</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Frais de déplacement possibles au-delà de 30 km
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
